import React, { useState } from "react";

import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  faChevronRight,
  faCopy,
  faPlus,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { Field, Form, Formik } from "formik";
import { partition } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { v4 } from "uuid";

import { openDispatchingModal, RuleBookAction } from "actions";

import Button from "components/Button";
import { InputDateRange } from "components/DateRangePicker/dateRangePicker";
import {
  CheckBox,
  SelectField,
  withNamespace,
} from "components/Form/FormikControls";
import { Column, Row } from "components/Layout";
import { Paper } from "components/Paper";

import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermissions,
  RuleBookPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { Reports } from "constants/reports";
import { RuleValueFormat } from "constants/ruleBooks";

import { openModalLink } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  getActiveLivestockAgentDeployment,
  getActiveSaleyardAdminSaleyard,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getRuleBookById,
  getRuleBooks,
  getRuleIdsByRuleBookId,
  getRulesByRuleBookId,
  getSaleSubTypeWithSpeciesOptions,
} from "selectors";

import { useToggle } from "hooks";

import { ValueSource } from "./constants";
import { getGstFee } from "./Rules/CustomRules/ValueClause";
import { RuleCard } from "./Rules/Layout";

export const RuleBookSummary = props => {
  const { namespace: ns, ruleBookId } = props;
  const dispatch = useDispatch();

  const ruleBookRules = useSelector(getRuleIdsByRuleBookId(ruleBookId)) || [];
  const ruleCount = ruleBookRules.length;

  const saleSubTypeOptions = useSelector(getSaleSubTypeWithSpeciesOptions);

  const billingReportOptions = Object.values(Reports)
    .filter(report => report.isRulebookDriven)
    .map(report => ({
      value: report.slug,
      label: report.title,
    }));

  function onClickAddManagedRule() {
    dispatch(
      openDispatchingModal(
        ModalTypes.RuleLibrary,
        window.location.hash,
        { ruleBookId },
        { ruleBookId },
      ),
    );
  }

  function onClickOpenRuleBookInModal() {
    openModalLink(ModalTypes.RuleBook, {
      id: ruleBookId,
    });
  }

  function onClickAddCustomRule() {
    dispatch(
      openDispatchingModal(ModalTypes.EditRule, window.location.hash, {
        rule: {
          criteria: [],
          category: "",
          comment: "",
          gl_code: "",
          billing_tags: [],
          from_business: { source: "", attribute: "", fieldId: "", index: "" },
          id: v4(),
          input_field: { attribute: "", fieldId: "" },
          name: "",
          price_multiplier: 1,
          quantityOutputFormat: RuleValueFormat.UNITS,
          quantityRawFormat: RuleValueFormat.UNITS,
          to_business: { source: "", attribute: "", fieldId: "", index: "" },
          unitAmountOutputFormat: RuleValueFormat.DOLLARS,
          unitAmountRawFormat: RuleValueFormat.CENTS,
          value: {
            amount: { source: ValueSource.CONSTANT, value: 0 },
            offset: { source: ValueSource.CONSTANT, value: 0 },
            rounding_value: 0,
            unit_offset: { source: ValueSource.CONSTANT, value: 0 },
            unit_limit: { source: ValueSource.CONSTANT, value: 0 },
            units: { source: "", attribute: "", fieldId: "", index: "" },
            fees: [getGstFee("exclusive")],
          },
          include_if_zero: false,
          rule_book_id: ruleBookId,
        },
      }),
    );
  }

  return (
    <>
      <Paper>
        <Grid item container xs={12} spacing={1}>
          <Grid item xs={8}>
            <h1 className="h-75">
              <Field
                as="textarea"
                className="w-full h-full p-2"
                name={withNamespace(ns, "name")}
                required
              />
            </h1>
          </Grid>
          <Grid item xs={4}>
            <p>{ruleCount} Rules </p>
          </Grid>
          <Grid item xs={12}>
            <Column textAlignCenter>
              <SelectField
                name="reports"
                label="Rulebook Reports"
                options={billingReportOptions}
                tooltip="Select the reports you would like this rulebook to run on"
                isMulti
              />
            </Column>
          </Grid>
          <Grid item container xs={12} spacing={1}>
            <InputDateRange
              startDateFieldName="effective_start_date"
              endDateFieldName="effective_end_date"
              includeTime={false}
              showYearOptions
              showRangeOptions={false}
              startDateLabel="Effective Start Date"
              endDateLabel="Effective End Date"
            />
          </Grid>
          <Grid spacing={1} item xs={12} container direction="column">
            <Grid
              item
              container
              justifyContent="space-between"
              alignItems="flex-end"
              spacing={1}
            >
              <Grid item xs={6}>
                <SelectField
                  label="Sale Subtypes"
                  name="sale_sub_type_ids"
                  options={saleSubTypeOptions}
                  isClearable
                  isMulti
                />
              </Grid>
              <Grid item>
                <CheckBox label="Is Archived?" name="is_archived" />
              </Grid>
              <Grid item>
                <TestRuleBookButton ruleBookId={ruleBookId} />
              </Grid>
              <Grid item>
                <Button type="submit" data-tour="save">
                  Save
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
      <Divider />
      <p />
      <Paper>
        <Grid item container xs={12} spacing={1}>
          <Grid item xs={4}>
            <Button
              onClick={onClickAddManagedRule}
              data-tour="addManagedRule"
              type="button"
            >
              Add Managed Rule
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              onClick={onClickAddCustomRule}
              data-tour="addCustomRule"
              type="button"
            >
              Add Custom Rule
            </Button>
          </Grid>
          <Grid item xs={4}>
            <Button
              onClick={onClickOpenRuleBookInModal}
              data-tour="openInGrid"
              type="button"
            >
              Open in Grid
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

function ViewRules(props) {
  const { ruleBookId } = props;
  const rules = useSelector(getRulesByRuleBookId(ruleBookId)) || [];
  return rules.map(rule => <RuleCard key={rule.id} ruleId={rule.id} />);
}

function TestRuleBookButton(props) {
  const { isDisabled, ruleBookId } = props;

  const handleOpenRuleTester = () => {
    // Stash the rule data in state - it's (probably) too big for the URL method.

    openModalLink(
      ModalTypes.RuleTester,
      [[], ruleBookId, false],
      window.location.hash,
    );
  };

  return (
    <Button
      data-tour="testRulebook"
      onClick={handleOpenRuleTester}
      disabled={isDisabled}
      type="button"
    >
      Test Rulebook
    </Button>
  );
}

function EditRuleBook(props) {
  const { ruleBookId } = props;
  const ruleBook = useSelector(getRuleBookById(ruleBookId));
  const dispatch = useDispatch();

  function onSubmit(ruleBook) {
    dispatch(RuleBookAction.update(ruleBook));
  }

  return (
    <Column fullWidth>
      <Formik enableReinitialize initialValues={ruleBook} onSubmit={onSubmit}>
        <Form>
          <RuleBookSummary ruleBookId={ruleBookId} />
        </Form>
      </Formik>
      <Divider />
      <ViewRules ruleBookId={ruleBookId} />
    </Column>
  );
}

function RuleBookListItem(props) {
  const {
    id,
    name,
    onCopyRuleBook,
    onRemoveRuleBook,
    onSelectRuleBook,
  } = props;

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);

  function onClickCopyRuleBook() {
    onCopyRuleBook(id);
  }

  function onClickSelectRuleBook() {
    onSelectRuleBook(id);
  }

  function onClickRemoveRuleBook() {
    onRemoveRuleBook(id);
  }

  function onClickShowActions(event) {
    if (actionsAnchorEl === null) {
      setActionsAnchorEl(event.target);
    } else {
      setActionsAnchorEl(null);
    }
    event.stopPropagation();
  }

  function onCloseActions() {
    setActionsAnchorEl(null);
  }

  const isActionsMenuVisible = actionsAnchorEl !== null;

  return (
    <>
      <ListItem data-tour={name} component="div" disableGutters>
        <ListItem button onClick={onClickSelectRuleBook}>
          <ListItemText>{name}</ListItemText>
          <IconButton aria-label="copy" edge="end" onClick={onClickShowActions}>
            <FontAwesomeIcon icon={faChevronRight} size="sm" />
          </IconButton>
        </ListItem>
      </ListItem>

      {typeof onCopyRuleBook === "function" ||
      typeof onRemoveRuleBook === "function" ? (
        <Popover
          anchorEl={actionsAnchorEl}
          onClose={onCloseActions}
          open={isActionsMenuVisible}
        >
          <List>
            {typeof onCopyRuleBook === "function" ? (
              <ListItem button onClick={onClickCopyRuleBook}>
                <ListItemIcon>
                  <IconButton aria-label="copy">
                    <FontAwesomeIcon icon={faCopy} />
                  </IconButton>
                  <ListItemText>Copy</ListItemText>
                </ListItemIcon>
              </ListItem>
            ) : null}

            {typeof onRemoveRuleBook === "function" ? (
              <ListItem button onClick={onClickRemoveRuleBook}>
                <ListItemIcon>
                  <IconButton aria-label="delete">
                    <FontAwesomeIcon icon={faTrash} />
                  </IconButton>
                  <ListItemText>Delete</ListItemText>
                </ListItemIcon>
              </ListItem>
            ) : null}
          </List>
        </Popover>
      ) : null}
    </>
  );
}

export function RuleBooksManager() {
  const dispatch = useDispatch();

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);

  const parentObjectSelector = isLivestockAgent
    ? getActiveLivestockAgentDeployment
    : getActiveSaleyardAdminSaleyard;
  const createPermission = isLivestockAgent
    ? DeploymentPermissions.canAddRuleBook
    : SaleyardPermissions.canAddRuleBook;

  const parentObject = useSelector(parentObjectSelector);

  const hasCreatePermissions = hasPermission(parentObject, createPermission);

  const ruleBookByIdLookup = useSelector(getRuleBooks) || {};
  const ruleBooks = Object.values(ruleBookByIdLookup);

  const [archivedRuleBooks, activeRuleBooks] = partition(
    ruleBooks,
    rulebook => rulebook.is_archived,
  );
  const [selectedRuleBookId, setSelectedRuleBookId] = useState(null);

  const deploymentId = (isLivestockAgent && parentObject.id) || null;
  const saleyardName = (isSaleyardAdmin && parentObject.name) || null;
  const createOptions = { deploymentId, saleyardName };

  function selectRuleBook(ruleBookId) {
    setSelectedRuleBookId(ruleBookId);
    document.getElementById("main-wrapper").scrollTop = 0;
  }

  function onClickAddRuleBook() {
    const id = v4();
    dispatch(
      RuleBookAction.create(
        {
          name: "New Rule Book",
          description: "",
          id,
          rules: [],
        },
        createOptions,
      ),
    );
    selectRuleBook(id);
  }

  function onChangeTab(_ignored, ruleBookId) {
    if (ruleBookId) {
      selectRuleBook(ruleBookId);
    }
  }

  function onSelectRuleBook(ruleBookId) {
    selectRuleBook(ruleBookId);
  }

  function onRemoveRuleBook(ruleBookId) {
    dispatch(RuleBookAction.delete(ruleBookId));
    if (selectedRuleBookId === ruleBookId) {
      // Unselect the rulebook if it is deleted
      selectRuleBook(null);
    }
  }

  function onCopyRuleBook(ruleBookId) {
    const id = v4();

    dispatch(RuleBookAction.copy(ruleBookId, id, createOptions));
    selectRuleBook(id);
  }

  const [showArchived, toggleShowArchived] = useToggle(false);

  return (
    <Row flexGrow>
      <Column margin={2}>
        <Paper square>
          <List
            orientation="vertical"
            value={selectedRuleBookId}
            onChange={onChangeTab}
          >
            {activeRuleBooks.map(ruleBook => (
              <RuleBookListItem
                key={`${ruleBook.id}_${selectedRuleBookId}`}
                name={ruleBook.name}
                id={ruleBook.id}
                onCopyRuleBook={hasCreatePermissions ? onCopyRuleBook : null}
                onRemoveRuleBook={
                  hasPermission(ruleBook, RuleBookPermissions.delete)
                    ? onRemoveRuleBook
                    : null
                }
                onSelectRuleBook={onSelectRuleBook}
              />
            ))}
            {hasCreatePermissions ? (
              <>
                <Divider />
                <ListItem aria-label="add" button onClick={onClickAddRuleBook}>
                  <ListItemIcon>
                    <FontAwesomeIcon icon={faPlus} />
                  </ListItemIcon>
                  <ListItemText>Create Rule Book</ListItemText>
                </ListItem>
              </>
            ) : null}
            <Divider />
            <ListItem aria-label="add" button onClick={toggleShowArchived}>
              <ListItemIcon>
                <FontAwesomeIcon
                  icon={showArchived ? faChevronDown : faChevronRight}
                />
              </ListItemIcon>
              <ListItemText>Show Archived Rulebooks</ListItemText>
            </ListItem>
            <Collapse in={showArchived}>
              {archivedRuleBooks.map(ruleBook => {
                return (
                  <RuleBookListItem
                    key={ruleBook.id}
                    name={ruleBook.name}
                    id={ruleBook.id}
                    onCopyRuleBook={
                      hasCreatePermissions ? onCopyRuleBook : null
                    }
                    onRemoveRuleBook={
                      hasPermission(ruleBook, RuleBookPermissions.delete)
                        ? onRemoveRuleBook
                        : null
                    }
                    onSelectRuleBook={onSelectRuleBook}
                  />
                );
              })}
            </Collapse>
          </List>
        </Paper>
      </Column>
      <Column full margin={2}>
        <Row full fullHeight>
          {selectedRuleBookId && (
            <EditRuleBook ruleBookId={selectedRuleBookId} />
          )}
        </Row>
      </Column>
    </Row>
  );
}
